'use client'
import { cn, Modal, ModalBody, ModalContent, useDisclosure } from "@heroui/react";
import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import PremiumPricing from "./PremiumPricing";
import EnergyPricing from "./EnergyPricing";
import { useResponsiveSize } from "@/provider/useResponsiveSize";
import PaymentIncompleteToast from "./PaymentIncompleteToast";
import PremiumNotActiveModal from "./PremiumNotActiveModal";
import VipExpireModal from "./VipExpireModal";
import { usePendingSubscribeFail } from "./usePendingSubscribeFail";
import { useVipExpirePrompt } from "./useVipExpirePrompt";
import { mergeModalLayerClassNames, useModalLayer } from "@/ui/modalLayer";


type pricingType = 'energy' | 'premium' | 'crystals'
type showPricingFnType = (pricingType: pricingType) => void

export const Context = createContext<showPricingFnType>(null!)
/** 关闭购买弹窗；弹窗内跳站内页（如兑换码入口）时用，弹窗挂在全局 layout，换路由不会自动关 */
const CloseContext = createContext<() => void>(() => { })

export const PricingModalProvider = ({ children }: PropsWithChildren) => {
  const t = useTranslations()
  const [type, setType] = useState<pricingType>('energy')
  const size = useResponsiveSize(['default', 'sm', 'lg'])
  const { isOpen, onOpenChange, onOpen, onClose } = useDisclosure()
  const failModal = useDisclosure()
  const expireModal = useDisclosure()
  const showPricing: showPricingFnType = (type) => {
    setType(type)
    onOpen()
  }
  const openPremiumPlans = useCallback(() => {
    failModal.onClose()
    setType('premium')
    onOpen()
  }, [failModal.onClose, onOpen])
  const failToast = useDisclosure()
  const expireToast = useDisclosure()
  const showFailToast = useCallback(() => {
    expireToast.onClose()
    expireModal.onClose()
    failToast.onOpen()
  }, [expireToast.onClose, expireModal.onClose, failToast.onOpen])
  const showExpireToast = useCallback(() => {
    expireToast.onOpen()
  }, [expireToast.onOpen])
  // 订阅失败优先于会员过期，失败已触发则不再走过期
  const subscribeFailStatus = usePendingSubscribeFail(showFailToast)
  useVipExpirePrompt(showExpireToast, subscribeFailStatus)
  const energyOpen = (type === 'energy' || type === 'crystals') && isOpen
  const premiumOpen = type === 'premium' && isOpen
  const energyLayer = useModalLayer(energyOpen)
  const premiumLayer = useModalLayer(premiumOpen)
  return <>
    <Context.Provider value={showPricing}>
    <CloseContext.Provider value={onClose}>
      {children}
      {energyOpen && <Modal
        isOpen={energyOpen}
        onOpenChange={onOpenChange}
        radius="none"
        classNames={mergeModalLayerClassNames({
          closeButton: 'z-50',
          base: cn("bg-surface sm:bg-transparent w-full sm:rounded-[12px] max-w-[680px]"),
          body: "p-0 w-full w-full bg-transparent h-full",
        }, energyLayer)}
        size={size === 'default' ? 'full' : undefined}
      >
        <ModalContent>
          {onClose => (
            <ModalBody>
              <EnergyPricing initialTab={type === 'crystals' ? 'crystals' : 'energy'} />
            </ModalBody>
          )}
        </ModalContent>
      </Modal>
      }

      {premiumOpen && <Modal
        isOpen={premiumOpen}
        onOpenChange={onOpenChange}
        radius="none"
        classNames={mergeModalLayerClassNames({
          closeButton: 'z-50',
          base: cn("bg-surface lg:bg-transparent w-full lg:rounded-[12px] max-w-[1130px]"),
          body: "p-0 w-full overflow-hidden bg-transparent",
        }, premiumLayer)}
        size={size === 'lg' ? undefined : 'full'}
      >
        <ModalContent>
          {onClose => (
            <ModalBody>
              <PremiumPricing />
            </ModalBody>
          )}
        </ModalContent>
      </Modal>}

      {failToast.isOpen && (
        <PaymentIncompleteToast
          onPress={() => {
            failToast.onClose()
            failModal.onOpen()
          }}
          onTimeout={failToast.onClose}
        />
      )}

      {expireToast.isOpen && !failToast.isOpen && (
        <PaymentIncompleteToast
          title={t("pricing.vipExpireToastTitle")}
          description={t("pricing.vipExpireToastDesc")}
          onPress={() => {
            expireToast.onClose()
            expireModal.onOpen()
          }}
          onTimeout={expireToast.onClose}
        />
      )}

      <PremiumNotActiveModal
        isOpen={failModal.isOpen}
        onOpenChange={failModal.onOpenChange}
        onViewPlans={openPremiumPlans}
      />

      <VipExpireModal
        isOpen={expireModal.isOpen && !failModal.isOpen && !failToast.isOpen}
        onOpenChange={expireModal.onOpenChange}
        onViewPlans={() => {
          expireModal.onClose()
          openPremiumPlans()
        }}
      />
    </CloseContext.Provider>
    </Context.Provider >

  </>
}


export const useShowPricing = () => useContext(Context)
export const useClosePricing = () => useContext(CloseContext)
